import { useState, useEffect, useCallback } from 'react';
import { useNotification } from '@/hooks';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { ShippingListApi } from '../models';
import { MAX_SHIPPINGS_PER_ROUTING } from '../constants/routing.constants';

interface UseShippingSelectionProps {
  sortedData: ShippingListApi[];
  canSelectRow?: (row: ShippingListApi) => boolean;
}

export const useShippingSelection = ({
  sortedData,
  canSelectRow,
}: UseShippingSelectionProps) => {
  const { showNotification } = useNotification();
  const [selectedShippings, setSelectedShippings] = useState<Set<string>>(
    new Set()
  );
  const [isAllSelected, setIsAllSelected] = useState(false);

  const selectableData = canSelectRow
    ? sortedData.filter(canSelectRow)
    : sortedData;

  useEffect(() => {
    if (selectableData.length === 0) {
      setIsAllSelected(false);
      return;
    }
    const allSelected = selectableData.every((shipping) =>
      selectedShippings.has(String(shipping.number))
    );
    setIsAllSelected(allSelected);
  }, [selectedShippings, selectableData]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        const newSelected = new Set(selectedShippings);
        selectableData.forEach((shipping) => {
          if (newSelected.size < MAX_SHIPPINGS_PER_ROUTING) {
            newSelected.add(String(shipping.number));
          }
        });
        if (newSelected.size >= MAX_SHIPPINGS_PER_ROUTING) {
          showNotification(
            `No se pueden seleccionar más de ${MAX_SHIPPINGS_PER_ROUTING} envíos a la vez`,
            NotificationSeverity.warning
          );
        }
        setSelectedShippings(newSelected);
      } else {
        const currentPageNumbers = new Set(
          sortedData.map((s) => String(s.number))
        );
        const newSelected = new Set(selectedShippings);
        currentPageNumbers.forEach((num) => newSelected.delete(num));
        setSelectedShippings(newSelected);
      }
    },
    [sortedData, selectableData, selectedShippings, showNotification]
  );

  const handleSelectShipping = useCallback(
    (shippingNumber: string, checked: boolean) => {
      const normalizedNumber = String(shippingNumber);

      if (checked) {
        const row = sortedData.find(
          (s) => String(s.number) === normalizedNumber
        );
        if (canSelectRow && row && !canSelectRow(row)) {
          return;
        }
        if (selectedShippings.size >= MAX_SHIPPINGS_PER_ROUTING) {
          showNotification(
            `No se pueden seleccionar más de ${MAX_SHIPPINGS_PER_ROUTING} envíos a la vez`,
            NotificationSeverity.warning
          );
          return;
        }
        setSelectedShippings(new Set([...selectedShippings, normalizedNumber]));
      } else {
        const newSelected = new Set(selectedShippings);
        newSelected.delete(normalizedNumber);
        setSelectedShippings(newSelected);
      }
    },
    [sortedData, canSelectRow, selectedShippings, showNotification]
  );

  return {
    selectedShippings,
    isAllSelected,
    handleSelectAll,
    handleSelectShipping,
    MAX_SHIPPINGS_PER_ROUTING,
  };
};
